import { useRef, useCallback } from "react";

type PreservedVersionSelection = {
    versionIndexMap: Record<string, number>;
    answerIndexMap: Record<string, number>;
    selectedAnswerIndex: number | null;
};

/**
 * Custom hook to preserve user's selected question version indices during abort operations.
 *
 * **Problem**: When user aborts a new question while generating, loadChat resets all version
 * indices to 0 (latest), losing track of which versions user was actually viewing.
 *
 * **Solution**: Store version indices before abort, restore after loadChat completes.
 *
 * **Lifecycle**:
 * - Snapshot taken when: User triggers new question (before API call)
 * - Restored when: Abort occurs and loadChat completes
 * - Cleared when: User navigates to different session, starts new chat, or closes tab
 */
export const useVersionPreservation = () => {
    // Stores the version indices snapshot for the current active session
    const preservedVersionIndicesRef = useRef<{
        sessionId: string | null;
        versionIndexMap: Record<string, number>;
        answerIndexMap: Record<string, number>;
        selectedAnswerIndex: number | null;
    }>({
        sessionId: null,
        versionIndexMap: {},
        answerIndexMap: {},
        selectedAnswerIndex: null
    });

    /**
     * Takes a snapshot of current version indices before starting a new question.
     * Should be called before makeApiRequest when user asks a new question.
     */
    const snapshotVersionIndices = useCallback(
        (
            sessionId: string | null,
            versionIndexMap: Record<string, number>,
            answerIndexMap: Record<string, number> = {},
            selectedAnswerIndex: number | null = null
        ) => {
            preservedVersionIndicesRef.current = {
                sessionId,
                versionIndexMap: { ...versionIndexMap },
                answerIndexMap: { ...answerIndexMap },
                selectedAnswerIndex
            };
        },
        []
    );

    /**
     * Restores the preserved version indices after abort and loadChat.
     * Returns the preserved version indices if sessionId matches, otherwise null.
     */
    const restoreVersionIndices = useCallback((currentSessionId: string | null): Record<string, number> | null => {
        const preserved = preservedVersionIndicesRef.current;

        // Backward-compatible API used by existing tests/components.
        if (preserved.sessionId === currentSessionId && Object.keys(preserved.versionIndexMap).length > 0) {
            return { ...preserved.versionIndexMap };
        }

        return null;
    }, []);

    const restorePreservedSelections = useCallback((currentSessionId: string | null): PreservedVersionSelection | null => {
        const { sessionId, versionIndexMap, answerIndexMap, selectedAnswerIndex } = preservedVersionIndicesRef.current;

        // Only restore if sessionId matches (user didn't navigate away)
        const hasQuestionVersionState = Object.keys(versionIndexMap).length > 0;
        const hasAnswerVersionState = Object.keys(answerIndexMap).length > 0;
        const hasSelectionState = selectedAnswerIndex !== null;

        if (sessionId === currentSessionId && (hasQuestionVersionState || hasAnswerVersionState || hasSelectionState)) {
            return {
                versionIndexMap: { ...versionIndexMap },
                answerIndexMap: { ...answerIndexMap },
                selectedAnswerIndex
            };
        }

        return null;
    }, []);

    /**
     * Clears the preserved version indices.
     * Should be called when:
     * - User navigates to different session
     * - User starts new chat
     * - User closes the chat page
     * - Question completes successfully (no abort)
     */
    const clearVersionIndices = useCallback(() => {
        preservedVersionIndicesRef.current = {
            sessionId: null,
            versionIndexMap: {},
            answerIndexMap: {},
            selectedAnswerIndex: null
        };
    }, []);

    return {
        snapshotVersionIndices,
        restoreVersionIndices,
        restorePreservedSelections,
        clearVersionIndices
    };
};
